// Player points wallet — synchronous localStorage, the single source of truth
// during play. Ported from the reference games' game/points.js with the same
// public API so a real cloud-sync layer is drop-in. Key prefix is the game id.
//
// GAMZONE_COMMON_CONTRACT.md §5:
//   - INITIAL_POINTS granted once per wallet; never re-granted at 0.
//   - getPoints/setPoints/addPoints/spendPoints/onPointsChange semantics fixed.
//   - unsynced-earn counter carries guest earnings onto the cloud balance at
//     login WITHOUT the welcome grant (which goes through setPoints, uncounted).
//   - per-game local top-5 score list, deduped/sorted/capped.

import { useSyncExternalStore } from "react";
import { key } from "../gameId.ts";

const KEY = key("points");
const INIT_KEY = key("pointsInit");
export const INITIAL_POINTS = 500;

const UNSYNCED_KEY = key("unsyncedEarn");
let syncActive = false;

const listeners = new Set<(v: number) => void>();

/** cloudPoints calls this when a signed-in session starts/stops mirroring. */
export function setSyncActive(active: boolean): void {
  syncActive = !!active;
}

function readUnsynced(): number {
  return parseInt(localStorage.getItem(UNSYNCED_KEY) || "0", 10) || 0;
}
function bumpUnsynced(delta: number): void {
  if (syncActive) return;
  try {
    localStorage.setItem(UNSYNCED_KEY, String(readUnsynced() + delta));
  } catch {
    /* private mode */
  }
}
export function getUnsyncedEarn(): number {
  return Math.max(0, readUnsynced());
}
export function clearUnsyncedEarn(): void {
  try {
    localStorage.setItem(UNSYNCED_KEY, "0");
  } catch {
    /* private mode */
  }
}

export function onPointsChange(fn: (v: number) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Live wallet balance — re-renders on any addPoints/spendPoints/setPoints. */
export function usePoints(): number {
  return useSyncExternalStore(onPointsChange, getPoints, getPoints);
}

/** One-time welcome grant for a brand-new wallet. Idempotent. */
export function ensureInitialPoints(): number {
  try {
    if (localStorage.getItem(INIT_KEY)) return getPoints();
    localStorage.setItem(INIT_KEY, "1");
  } catch {
    return getPoints();
  }
  return setPoints(INITIAL_POINTS);
}

export function getPoints(): number {
  return parseInt(localStorage.getItem(KEY) || "0", 10) || 0;
}

export function setPoints(n: number, opts: { silent?: boolean } = {}): number {
  const v = Math.max(0, Math.floor(n) || 0);
  try {
    localStorage.setItem(KEY, String(v));
  } catch {
    /* private mode */
  }
  if (!opts.silent) for (const fn of listeners) fn(v);
  return v;
}

export function addPoints(n: number): number {
  bumpUnsynced(Math.floor(n) || 0);
  return setPoints(getPoints() + n);
}

export function spendPoints(n: number): boolean {
  const cur = getPoints();
  if (cur < n) return false;
  bumpUnsynced(-(Math.floor(n) || 0));
  setPoints(cur - n);
  return true;
}

// ---- high scores (local, top 5) ----

const SCORES_KEY = key("scores");
export const MAX_SCORES = 5;
const scoreListeners = new Set<(v: number[]) => void>();

export function onScoresChange(fn: (v: number[]) => void): () => void {
  scoreListeners.add(fn);
  return () => scoreListeners.delete(fn);
}

function normalizeScores(list: unknown): number[] {
  const nums = (Array.isArray(list) ? list : [])
    .map((n) => Math.max(0, Math.floor(Number(n) || 0)))
    .filter((n) => n > 0);
  return [...new Set(nums)].sort((a, b) => b - a).slice(0, MAX_SCORES);
}

export function getScores(): number[] {
  let list: unknown;
  try {
    list = JSON.parse(localStorage.getItem(SCORES_KEY) || "null");
  } catch {
    list = null;
  }
  return normalizeScores(list);
}

export function setScores(list: number[], opts: { silent?: boolean } = {}): number[] {
  const v = normalizeScores(list);
  try {
    localStorage.setItem(SCORES_KEY, JSON.stringify(v));
  } catch {
    /* private mode */
  }
  if (!opts.silent) for (const fn of scoreListeners) fn(v);
  return v;
}

export function addScore(n: number): number[] {
  return setScores([...getScores(), n]);
}

export function getHiScore(): number {
  return getScores()[0] || 0;
}
